import type { VariantProps } from 'class-variance-authority'
import type { ICalendarEvent } from '@/features/calendar/interfaces'
import { cva } from 'class-variance-authority'

import { endOfDay, format, isSameDay, parseISO, startOfDay } from 'date-fns'

import { EventDetailsDialog } from '@/features/calendar/components/dialogs/event-details-dialog'

import { cn } from '@/lib/utils'
import { useCalendar } from '../../contexts/use-calendar'

const trainingBadgeVariants = cva(
  'mx-1 flex w-auto h-6.5 select-none items-center justify-between gap-1.5 truncate whitespace-nowrap rounded-md border px-2 text-xs',
  {
    variants: {
      color: {
        // Colored variants
        'blue': 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300',
        'green': 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300',
        'red': 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300',
        'yellow': 'border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-300',
        'purple': 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300',
        'orange': 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300',

        // Dot variants
        'blue-dot': 'border-b-primary bg-bg-secondary text-t-primary [&_svg]:fill-blue-600',
        'green-dot': 'border-b-primary bg-bg-secondary text-t-primary [&_svg]:fill-green-600',
        'red-dot': 'border-b-primary bg-bg-secondary text-t-primary [&_svg]:fill-red-600',
        'orange-dot': 'border-b-primary bg-bg-secondary text-t-primary [&_svg]:fill-orange-600',
        'purple-dot': 'border-b-primary bg-bg-secondary text-t-primary [&_svg]:fill-purple-600',
        'yellow-dot': 'border-b-primary bg-bg-secondary text-t-primary [&_svg]:fill-yellow-600',
      },
      multiDayPosition: {
        first: 'relative z-10 mr-0 w-[calc(100%_+_1px)] rounded-r-none border-r-0 [&>span]:mr-2.5',
        middle: 'relative z-10 mx-0 w-[calc(100%_+_1px)] min-w-[8px] grow rounded-none border-0',
        last: 'ml-0 rounded-l-none border-l-0',
        none: '',
      },
    },
    defaultVariants: {
      color: 'blue-dot',
    },
  },
)

interface IProps extends Omit<VariantProps<typeof trainingBadgeVariants>, 'color' | 'multiDayPosition'> {
  cellDate: Date
  className?: string
  position?: 'first' | 'middle' | 'last' | 'none'
  training: ICalendarEvent
  trainingCurrentDay?: number
  trainingTotalDays?: number
}

export function MonthTrainingBadge({ cellDate, className, position: propPosition, training, trainingCurrentDay, trainingTotalDays }: IProps) {
  const { badgeVariant } = useCalendar()

  const itemStart = startOfDay(parseISO(training.startAt))
  const itemEnd = endOfDay(parseISO(training.endAt))


  const colorClass: Record<string, string> = {
    blue: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-100',
    green: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-100',
    red: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-100',
    yellow: 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-100',
    purple: 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-100',
    orange: 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-100',
  }

  if (cellDate < itemStart || cellDate > itemEnd)
    return null

  let position: 'first' | 'middle' | 'last' | 'none' | undefined

  if (propPosition) {
    position = propPosition
  }
  else if (trainingCurrentDay && trainingTotalDays) {
    position = 'none'
  }
  else if (isSameDay(itemStart, itemEnd)) {
    position = 'none'
  }
  else if (isSameDay(cellDate, itemStart)) {
    position = 'first'
  }
  else if (isSameDay(cellDate, itemEnd)) {
    position = 'last'
  }
  else {
    position = 'middle'
  }

  // Render the title only on the first day of a multi-day event (and on single-day events)
  const renderBadgeText = position === 'first' || position === 'none'

  // Multi-day segments should always render as filled pills across days.
  // Use the colored variant for first/middle/last; keep the global variant for single-day/none.
  const isMultiDaySegment = position !== 'none'
  const resolvedColor = isMultiDaySegment ? training.color : (badgeVariant === 'dot' ? `${training.color}-dot` : training.color)
  const color = resolvedColor as VariantProps<typeof trainingBadgeVariants>['color']

  const eventBadgeClasses = cn(trainingBadgeVariants({ color, multiDayPosition: position, className }), cn(
    'pointer-events-auto flex h-4.5 items-center justify-between truncate whitespace-nowrap rounded-md border px-2 text-xs',
    colorClass[training.color] ?? colorClass.blue,
    'rounded-l-md', 'rounded-r-md',
  ))

  return (
    <EventDetailsDialog
      training={training}
    >
      <div role="button" tabIndex={0} className={eventBadgeClasses}>
        <div className="flex items-center gap-1.5 truncate">
          {renderBadgeText && (
            <p className="flex-1 whitespace-pre-wrap px-2 pb-2 mt-2 font-semibold">
              {trainingCurrentDay && (
                <span className="text-xs">
                  Day
                  {' '}
                  {trainingCurrentDay}
                  {' '}
                  of
                  {' '}
                  {trainingTotalDays}
                  {' '}
                  •
                  {' '}
                </span>
              )}
              {training.title}
            </p>
          )}
        </div>

        {/* Show time only on last or single-day segments to avoid repetition */}
        {(position === 'last' || position === 'none') && (
          <span>{format(new Date(training.endAt), 'hh:mm a')}</span>
        )}
      </div>
    </EventDetailsDialog>
  )
}
